<template>
  <div class="row f-row justify-content-center h-100">
    <!-- First Column -->
    <transition name="fadeCharts" v-if="!hideLeft">
      <div
        class="col text-center"
        v-bind:class="[expandLeft ? 'col-12' : 'col-5']"
      >
        <div class="h-100 d-flex flex-column">
          <!-- 2 Charts Column -->
          <div class="row f-row h-100">
            <div class="col col-12">
              <!-- Chart 1 -->
              <div class="h-50 d-flex flex-column" style="width: 100%;">
                <!-- Row: title -->
                <div class="row f-row">
                  <div class="markets-chart-title text-left d-flex">
                    <span class="mr-auto">
                      BURSA MALAYSIA (BMD) HISTORICAL AND SWAPS
                    </span>
                    <div class="sidebarHidden">
                      <button
                        @click="expandLeftAnimation()"
                        class="btn mr-1"
                        style="width: 12px;"
                      >
                        <i class="fa fa-expand" v-if="!expandLeft"></i>
                        <i class="fa fa-compress" v-if="expandLeft"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Row: controls and labels -->
                <div class="row f-row spot-row mt-1">
                  <div
                    class="col"
                    v-bind:class="[expandLeft ? 'col-2' : 'col-3']"
                  >
                    <div
                      class="btn-toolbar"
                      role="toolbar"
                      aria-label="Toolbar with button groups"
                    >
                      <div
                        class="btn-group btn-group-toggle btn-block ml-1 mr-1"
                        role="group"
                      >
                        <button
                          type="button"
                          class="btn btn-candle"
                          v-on:click="onUserSelectCPOCurr('usd')"
                          v-bind:class="[
                            cpoInUSDOrMYR === 'usd' ? 'active' : ''
                          ]"
                        >
                          USD / mt
                        </button>
                        <button
                          type="button"
                          class="btn btn-candle"
                          v-on:click="onUserSelectCPOCurr('myr')"
                          v-bind:class="[
                            cpoInUSDOrMYR === 'myr' ? 'active' : ''
                          ]"
                        >
                          MYR / mt
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    class="col txt-label-primary-2"
                    v-bind:class="[expandLeft ? 'col-3' : 'col-auto']"
                  >
                    <div class="triangle-topleft-analytics ml-1"></div>
                    <span class="inline-ul ml-1">
                      {{
                        interval === "day"
                          ? "DAILY"
                          : interval === "week"
                          ? "WEEKLY"
                          : "MONTHLY"
                      }}
                    </span>
                    <div class="triangle-topleft-dark-fwd ml-2"></div>
                    <span class="inline-ul ml-1">DAILY</span>
                    <div class="square-border-dark-fwd-slim ml-2"></div>
                    <span class="inline-ul ml-1">SWAPS</span>
                  </div>
                  <div class="col col-3" v-show="expandAnimation">
                    <HistoricalInterval
                      :showTitle="false"
                      :initPickedDateInterval="pickedIntervalHistory"
                      @eventOnChangeInterval="onHistoricalIntervalChange"
                    />
                  </div>
                  <div class="col col-3 ml-2" v-show="expandAnimation">
                    <RangeSlider
                      :labelTxt="'ZOOM:'"
                      :dates="xRange.length > 1 ? xRange.slice(0, -1) : xRange"
                      :minIdx="zoomInitial"
                      :maxIdx="zoomFinal"
                      @eventOnMinRange="eventMinRange"
                      @eventOnMaxRange="eventMaxRange"
                    />
                  </div>
                </div>
                <!-- Row: Chart -->
                <CPOPastAndFwdsChart
                  :allData="allData"
                  :colorTheme="colorTheme"
                  :div1Name="'malaysia_hist-div'"
                  :div2Name="'malaysia_fwd-div'"
                  :fwdColor="darkFwd"
                  :fwdData="
                    cpoInUSDOrMYR === 'usd'
                      ? malaysiaFwdUSDData
                      : malaysiaFwdData
                  "
                  :histData="
                    cpoInUSDOrMYR === 'usd'
                      ? malaysiaUSDHistDataSliced
                      : malaysiaHistDataSliced
                  "
                  :hoverName="'MAL'"
                  :interval="interval"
                  :isCandles="true"
                  :monthData="
                    cpoInUSDOrMYR === 'usd'
                      ? malaysiaUSDHistDataMonth
                      : malaysiaHistDataMonth
                  "
                  :processData="processData"
                  :progressPosition="22.5"
                  :renderChartsSignal="renderPriceChartSignal"
                  :showLoading="showLoadingAllCalcs"
                  :svg1Name="'svg1-malaysia_hist'"
                  :svg2Name="'svg2-malaysia_fwd'"
                />
              </div>

              <!-- Chart 2 -->
              <div class="h-50 d-flex flex-column" style="width: 100%;">
                <!-- Row: title -->
                <div class="row f-row">
                  <div class="markets-chart-title col col-12">
                    USD/{{ selectedCOPOrMYR.toUpperCase() }} HISTORICAL AND
                    FORWARDS
                  </div>
                </div>
                <!-- Row: controls and labels -->
                <div class="row f-row mt-1">
                  <div
                    class="col"
                    v-bind:class="[expandLeft ? 'col-2' : 'col-3']"
                  >
                    <div
                      class="btn-toolbar"
                      role="toolbar"
                      aria-label="Toolbar with button groups"
                    >
                      <div
                        class="btn-group btn-group-toggle btn-block ml-1 mr-1"
                        role="group"
                      >
                        <button
                          type="button"
                          class="btn btn-candle"
                          v-on:click="onUserSelectCurr('cop')"
                          v-bind:class="[
                            selectedCOPOrMYR === 'cop' ? 'active' : ''
                          ]"
                        >
                          USD/COP
                        </button>
                        <button
                          type="button"
                          class="btn btn-candle"
                          v-on:click="onUserSelectCurr('myr')"
                          v-bind:class="[
                            selectedCOPOrMYR === 'myr' ? 'active' : ''
                          ]"
                        >
                          USD/MYR
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col col-auto txt-label-primary-2">
                    <div class="triangle-topleft-analytics ml-1"></div>
                    <span class="inline-ul ml-1">
                      {{
                        interval === "day"
                          ? "DAILY"
                          : interval === "week"
                          ? "WEEKLY"
                          : "MONTHLY"
                      }}
                    </span>
                    <div class="triangle-topleft-fwd ml-2"></div>
                    <span class="inline-ul ml-1">DAILY</span>
                    <div class="square-border-fwd-slim ml-2"></div>
                    <span class="inline-ul ml-1">FORWARDS</span>
                  </div>
                </div>
                <!-- Row: Chart -->
                <CPOPastAndFwdsChart
                  :allData="allData"
                  :colorTheme="colorTheme"
                  :div1Name="'rate_hist-div'"
                  :div2Name="'rate_fwd-div'"
                  :fwdColor="colorFwd"
                  :fwdData="selectedCOPOrMYR === 'cop' ? fwdUSDCOP : fwdUSDMYR"
                  :histData="
                    selectedCOPOrMYR === 'cop'
                      ? histUSDCOPSliced
                      : histUSDMYRSliced
                  "
                  :hoverName="'RATE'"
                  :interval="interval"
                  :isCandles="true"
                  :monthData="
                    selectedCOPOrMYR === 'cop'
                      ? histUSDCOPMonth
                      : histUSDMYRMonth
                  "
                  :processData="processData"
                  :progressPosition="72.5"
                  :renderChartsSignal="renderRatesChartSignal"
                  :showLoading="showLoadingAllCalcs"
                  :svg1Name="'svg1-rate_hist'"
                  :svg2Name="'svg2-rate_fwd'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ======================== //// ============================== -->
    <!-- Second Column -->
    <transition name="fadeCharts" v-if="!hideRight">
      <div
        class="col text-center results-divider"
        v-bind:class="[expandRight ? 'col-12' : '', 'col-7']"
      >
        <!-- Chart -->
        <div class="d-flex flex-column" style="height: calc(100% - 160px)">
          <!-- Row: title and resize button -->
          <div class="row f-row">
            <div class="markets-chart-title text-left d-flex">
              <span class="mr-auto">
                FEP HISTORICAL AND SWAPS
              </span>
              <div class="sidebarHidden">
                <button
                  @click="expandRightAnimation()"
                  class="btn mr-1"
                  style="width: 12px;"
                >
                  <i class="fa fa-expand" v-if="!expandRight"></i>
                  <i class="fa fa-compress" v-if="expandRight"></i>
                </button>
              </div>
            </div>
          </div>
          <!-- Row: Historical time selector -->
          <div class="row f-row mt-1">
            <div
              class="col col-auto txt-label-primary-2"
              style="justify-content: left!important;"
            >
              <i class="fa fa-minus ml-1 mr-1" style="color:var(--expo)"></i>
              MONTHLY
              <div class="square-border-hedge-slim ml-2"></div>
              <span class="inline-ul ml-1">SWAPS</span>
            </div>
            <div class="col col-4" style="height:90%; width:100%;">
              <HistoricalInterval
                :showTitle="false"
                :initPickedDateInterval="pickedIntervalHistory"
                @eventOnChangeInterval="onHistoricalIntervalChange"
              />
            </div>
            <div class="col col-cx ml-2 mr-2" style="height:90%; width:100%;">
              <RangeSlider
                :labelTxt="'ZOOM:'"
                :dates="
                  xRangeFEP.length > 1 ? xRangeFEP.slice(0, -1) : xRangeFEP
                "
                :minIdx="zoomInitial"
                :maxIdx="zoomFinal"
                @eventOnMinRange="eventMinRange"
                @eventOnMaxRange="eventMaxRange"
              />
            </div>
          </div>
          <!-- Row: Chart -->
          <CPOPastAndFwdsChart
            :allData="allData"
            :colorTheme="colorTheme"
            :div1Name="'fep_hist-div'"
            :div2Name="'fep_fwd-div'"
            :fwdColor="colorHedge(colorTheme)"
            :fwdData="allData"
            :histData="histFEP"
            :hoverName="'ROTT'"
            :interval="'month'"
            :isCandles="false"
            :isFEP="true"
            :monthData="monthFEP"
            :processData="processData"
            :progressPosition="37"
            :renderChartsSignal="renderFEPSignal"
            :showLoading="showLoadingAllCalcs"
            :svg1Name="'svg1-fep_hist'"
            :svg2Name="'svg2-fep_fwd'"
          />
        </div>

        <!-- Table -->
        <div
          class="d-flex flex-column bdr-t"
          style="width: 100%; height: 160px"
        >
          <CPOTableFEP
            :freight="freight"
            :tariff="tariff"
            :aveDataFEP="aveDataFEP"
            :inDate="xRangeFEP[zoomInitial]"
            :endDate="xRangeFEP[zoomFinal]"
            :isFEP="true"
            @eventOnUserFreightChange="onUserFreightChange"
            @eventOnUserTariffChange="onUserTariffChange"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CPOTableFEP from "@/components/markets/CPOTableFEP";
import CPOPastAndFwdsChart from "@/components/markets/CPOPastAndFwdsChart";
import HistoricalInterval from "@/components/intervals/AnalyticsHistoricalInterval";
import RangeSlider from "@/components/analytics/RangeSlider";
import {
  colorHedge,
  colorFwd,
  darkFwd
} from "@/_core/_helpers/_parametric_tools";

export default {
  name: "FEP",
  components: {
    CPOPastAndFwdsChart,
    HistoricalInterval,
    RangeSlider,
    CPOTableFEP
  },
  props: {
    allData: { required: true, default: () => [] },
    aveDataFEP: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    cpoInUSDOrMYR: { required: true },
    expandLeft: { required: true },
    expandRight: { required: true },
    freight: { required: true },
    fwdUSDCOP: { required: true, default: () => [] },
    fwdUSDMYR: { required: true, default: () => [] },
    hideLeft: { required: true },
    hideRight: { required: true },
    histFEP: { required: true, default: () => [] },
    histUSDCOPMonth: { required: true, default: () => [] },
    histUSDCOPSliced: { required: true, default: () => [] },
    histUSDMYRMonth: { required: true, default: () => [] },
    histUSDMYRSliced: { required: true, default: () => [] },
    interval: { required: true },
    malaysiaFwdData: { required: true, default: () => [] },
    malaysiaFwdUSDData: { required: true, default: () => [] },
    malaysiaHistDataMonth: { required: true, default: () => [] },
    malaysiaHistDataSliced: { required: true, default: () => [] },
    malaysiaUSDHistDataMonth: { required: true, default: () => [] },
    malaysiaUSDHistDataSliced: { required: true, default: () => [] },
    monthFEP: { required: true, default: () => [] },
    pickedIntervalHistory: { required: true },
    processData: { required: true },
    renderFEPSignal: { required: true, default: 0 },
    renderPriceChartSignal: { required: true, default: 0 },
    renderRatesChartSignal: { required: true, default: 0 },
    selectedCOPOrMYR: { required: true },
    showLoadingAllCalcs: { required: true, type: Boolean, default: false },
    tariff: { required: true },
    xRange: { required: true },
    xRangeFEP: { required: true },
    zoomFinal: { required: true },
    zoomInitial: { required: true }
  },
  data() {
    return {
      colorFwd: colorFwd,
      colorHedge: colorHedge,
      darkFwd: darkFwd,
      expandAnimation: false,
      pickedIntervalHistoryAux: ""
    };
  },
  watch: {
    expandLeft(val) {
      if (val) {
        setTimeout(() => {
          this.expandAnimation = val;
        }, 2000);
      } else {
        this.expandAnimation = val;
      }
    }
  },
  methods: {
    // === user interactions ===
    expandLeftAnimation() {
      this.$emit("eventOnExpandLeftAnimation");
    },
    expandRightAnimation() {
      this.$emit("eventOnExpandRightAnimation");
    },
    onUserSelectCPOCurr(val) {
      this.$emit("eventOnUserSelectCPOCurr", val);
    },
    onUserSelectCurr(val) {
      this.$emit("eventOnUserSelectCurr", val);
    },
    onHistoricalIntervalChange(val) {
      this.$emit("eventOnChangeInterval", val);
    },
    eventMinRange(val) {
      this.$emit("eventOnMinRange", val);
    },
    eventMaxRange(val) {
      this.$emit("eventOnMaxRange", val);
    },
    onUserFreightChange(val) {
      this.$emit("eventOnUserFreightChange", val);
    },
    onUserTariffChange(val) {
      this.$emit("eventOnUserTariffChange", val);
    }
  }
};
</script>

<style scoped></style>
